const homepageHero = require('../components/_homepageHero');

const initHomepageReviews = () => {
    const section = document.querySelector('[data-homepage-reviews]');
    if (!section) return;

    const track = section.querySelector('.homepage-review-track');
    const cards = Array.from(track.children);
    const previous = section.querySelector('.homepage-review-previous');
    const next = section.querySelector('.homepage-review-next');
    const offset = (card) => card.offsetLeft - cards[0].offsetLeft;
    const sync = () => {
        const visibleCards = cards.filter(card =>
            offset(card) + card.offsetWidth > track.scrollLeft + 1 &&
            offset(card) < track.scrollLeft + track.clientWidth - 1);
        const height = Math.max(...visibleCards.map(card => card.offsetHeight));
        const focusedElement = document.activeElement;
        previous.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        previous.hidden = previous.disabled;
        next.hidden = next.disabled;
        const scrollable = track.scrollWidth > track.clientWidth + 2;
        track.tabIndex = scrollable ? 0 : -1;
        if (scrollable) {
            section.setAttribute('aria-roledescription', 'Karussell');
        } else {
            section.removeAttribute('aria-roledescription');
        }
        if ((previous.hidden && focusedElement === previous) || (next.hidden && focusedElement === next)) {
            track.focus({preventScroll: true});
        }
        track.style.height = scrollable ? `${height}px` : '';
    };
    const move = (direction) => {
        const current = cards.reduce((nearest, card, index) =>
            Math.abs(offset(card) - track.scrollLeft) < Math.abs(offset(cards[nearest]) - track.scrollLeft) ? index : nearest, 0);
        track.scrollTo({left: offset(cards[Math.max(0, Math.min(cards.length - 1, current + direction))])});
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    let swipeStart = null;
    track.addEventListener('touchstart', (event) => {
        swipeStart = event.touches.length === 1 ? event.touches[0] : null;
    }, {passive: true});
    track.addEventListener('touchmove', (event) => {
        if (!swipeStart) return;
        if (event.touches.length !== 1) {
            swipeStart = null;
            return;
        }
        const dx = event.touches[0].clientX - swipeStart.clientX;
        const dy = event.touches[0].clientY - swipeStart.clientY;
        if (Math.abs(dy) > Math.abs(dx)) {
            swipeStart = null;
        } else if (dx !== 0 && event.cancelable) {
            event.preventDefault();
        }
    }, {passive: false});
    track.addEventListener('touchend', (event) => {
        const start = swipeStart;
        swipeStart = null;
        if (!start || event.touches.length) return;
        const dx = event.changedTouches[0].clientX - start.clientX;
        const dy = event.changedTouches[0].clientY - start.clientY;
        if (Math.abs(dx) >= 30 && Math.abs(dx) > Math.abs(dy)) {
            move(dx < 0 ? 1 : -1);
        }
    }, {passive: true});
    track.addEventListener('touchcancel', () => { swipeStart = null; }, {passive: true});
    track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
            track.scrollTo({left: event.key === 'Home' ? 0 : track.scrollWidth});
        } else {
            move(event.key === 'ArrowRight' ? 1 : -1);
        }
    });
    track.addEventListener('scroll', sync, {passive: true});
    const resizeObserver = new ResizeObserver(sync);
    cards.forEach(card => resizeObserver.observe(card));
    sync();
};

const initProductComparison = () => {
    const section = document.querySelector('[data-product-comparison]');

    if (!section) {
        return;
    }

    const carousel = section.querySelector('.carousel');
    const cards = Array.from(section.querySelectorAll('.product'));
    const platformLinks = Array.from(section.querySelectorAll('.platform-nav a'));
    const previous = section.querySelector('[data-carousel-previous]');
    const next = section.querySelector('[data-carousel-next]');
    const hint = section.querySelector('.scroll-hint');

    if (!carousel || !cards.length || !previous || !next || !hint) {
        return;
    }

    let current = 0;
    let targetCard = null;
    const cardOffset = (card) => Math.min(card.offsetLeft - cards[0].offsetLeft, carousel.scrollWidth - carousel.clientWidth);

    const syncCarouselState = () => {
        if (targetCard && Math.abs(carousel.scrollLeft - cardOffset(targetCard)) < 2) {
            targetCard = null;
        }
        current = cards.reduce((nearest, card, index) => {
            const nearestDistance = Math.abs(cards[nearest].offsetLeft - cards[0].offsetLeft - carousel.scrollLeft);
            const distance = Math.abs(card.offsetLeft - cards[0].offsetLeft - carousel.scrollLeft);
            return distance < nearestDistance ? index : nearest;
        }, 0);
        previous.disabled = carousel.scrollLeft < 2;
        next.disabled = carousel.scrollLeft >= carousel.scrollWidth - carousel.clientWidth - 2;
        hint.style.visibility = next.disabled ? 'hidden' : '';
        platformLinks.forEach((link) => {
            if (link.dataset.platform === (targetCard || cards[current]).dataset.platform) {
                link.setAttribute('aria-current', 'true');
            } else {
                link.removeAttribute('aria-current');
            }
        });
    };

    const showCard = (index, behavior = 'auto') => {
        targetCard = cards[Math.max(0, Math.min(index, cards.length - 1))];
        const left = cardOffset(targetCard);
        syncCarouselState();
        carousel.scrollTo({left, behavior});
    };

    platformLinks.forEach((link) => link.addEventListener('click', (event) => {
        event.preventDefault();
        const index = cards.findIndex((card) => card.id === link.hash.slice(1));
        const keyboardActivation = event.detail === 0;
        showCard(index, keyboardActivation ? 'instant' : 'auto');
        if (keyboardActivation) {
            cards[index].querySelector('a, button').focus();
        }
    }));
    previous.addEventListener('click', () => showCard(current - 1));
    next.addEventListener('click', () => showCard(current + 1));
    ['pointerdown', 'wheel', 'keydown'].forEach((event) => carousel.addEventListener(event, () => {
        targetCard = null;
        syncCarouselState();
    }, {passive: true}));
    carousel.addEventListener('keydown', (event) => {
        if (event.target !== carousel) {
            return;
        }

        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            cards[current].querySelector('a, button').focus();
            return;
        }

        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            return;
        }

        event.preventDefault();
        showCard(event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1), 'instant');
        syncCarouselState();
    });
    carousel.addEventListener('scroll', syncCarouselState, {passive: true});
    window.addEventListener('resize', () => window.requestAnimationFrame(() => {
        showCard(targetCard ? cards.indexOf(targetCard) : current, 'instant');
        syncCarouselState();
    }));
    syncCarouselState();
};

const initOrderProcess = () => {
    const root = document.querySelector('[data-order-process]');

    if (!root) {
        return;
    }

    const queryRoot = (selector) => root.querySelector(selector);
    const navigation = Array.from(root.querySelectorAll('[data-step]'));
    const scenes = Array.from(root.querySelectorAll('.scene'));
    const fills = Array.from(root.querySelectorAll('.progress-fill'));
    const followerFinish = Math.ceil(2500 + (500 - 39) * 1000 / 60);
    const durations = [4600, 6500, followerFinish + 1200];
    const starts = [0, durations[0], durations[0] + durations[1]];
    const total = durations.reduce((sum, duration) => sum + duration, 0);
    // After 2.5s of irregular arrivals, count every remaining follower individually.
    const followerArrivals = [
        [0, 0], [350, 3], [820, 8], [1140, 14], [1760, 22], [2130, 30], [2460, 39],
    ];
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let liveReducedMotion;
    let reduceMotionPreferred = reducedMotion.matches;
    const touch = matchMedia('(max-width: 599px), (pointer: coarse)');
    const formatNumber = new Intl.NumberFormat('de-DE');
    const stage = queryRoot('.stage');
    const cursor = queryRoot('.cursor');
    const tap = queryRoot('.tap');
    const playback = queryRoot('.playback');
    const packageValue = queryRoot('#order-process-package-value');
    const dropdown = queryRoot('.dropdown');
    const summary = queryRoot('.selection-summary');
    const applePay = queryRoot('#order-process-apple-pay');
    const followerCount = queryRoot('#order-process-follower-count');
    const fields = ['username', 'name', 'email'].map((name) => ({
        control: queryRoot(`#order-process-${name}-control`),
        value: queryRoot(`#order-process-${name}-value`),
    }));
    const targets = ['package-control', 'package-option', 'username-control', 'name-control', 'email-control', 'apple-pay'];
    let points = {};
    let elapsed = 0;
    let currentScene = -1;
    let followerGain = 0;
    let paused = reducedMotion.matches;
    let visible = false;
    let previewOnly = false;
    let frame = 0;
    let visibilityObserver;
    let previousTime;

    const clamp = (value) => Math.max(0, Math.min(1, value));
    const ease = (value) => 1 - (1 - clamp(value)) ** 4;
    const setText = (element, text) => {
        if (element.textContent !== text) {
            element.textContent = text;
        }
    };
    const measure = () => {
        const bounds = stage.getBoundingClientRect();
        points = Object.fromEntries(targets.map((id) => {
            const rect = queryRoot(`#order-process-${id}`).getBoundingClientRect();
            return [id, {
                x: rect.left - bounds.left + rect.width * (id === 'apple-pay' ? 0.56 : 0.7),
                y: rect.top - bounds.top + rect.height * 0.55,
            }];
        }));
    };
    const type = (field, text, time, start, duration) => {
        const progress = clamp((time - start) / duration);
        const typing = time >= start && progress < 1;
        setText(field.value, time < start ? '' : text.slice(0, Math.floor(progress * text.length)));
        field.value.classList.toggle('placeholder', time < start);
        field.value.classList.toggle('typing', typing);
        field.control.classList.toggle('focused', typing);
    };
    const move = (from, to, time, start, duration, clickTime) => {
        const firstPoint = points[from];
        const secondPoint = points[to];

        if (!firstPoint || !secondPoint) {
            return;
        }

        const progress = ease((time - start) / duration);
        const x = firstPoint.x + (secondPoint.x - firstPoint.x) * progress;
        const y = firstPoint.y + (secondPoint.y - firstPoint.y) * progress;
        cursor.style.transform = `translate(${x}px, ${y}px)`;
        cursor.style.opacity = time < 150 ? 0 : 1;
        const pulse = clamp((time - clickTime) / 430);
        tap.style.transform = `translate(${secondPoint.x}px, ${secondPoint.y}px) scale(${0.65 + pulse * 0.65})`;
        tap.style.opacity = time >= clickTime && pulse < 1 ? (1 - pulse) * (touch.matches ? 0.9 : 0.55) : 0;
    };
    const render = (animateFollowers = false) => {
        const index = elapsed < starts[1] ? 0 : elapsed < starts[2] ? 1 : 2;
        const time = reducedMotion.matches || previewOnly ? durations[index] - 1 : elapsed - starts[index];

        if (currentScene !== index) {
            currentScene = index;
            scenes.forEach((scene, sceneIndex) => {
                scene.classList.toggle('active', sceneIndex === index);
                scene.setAttribute('aria-hidden', String(sceneIndex !== index));
                scene.inert = sceneIndex !== index;
            });
            navigation.forEach((button) => {
                if (Number(button.dataset.step) === index) {
                    button.setAttribute('aria-current', 'step');
                } else {
                    button.removeAttribute('aria-current');
                }
            });
            requestAnimationFrame(measure);
        }

        fills.forEach((fill, fillIndex) => {
            fill.style.transform = `scaleX(${fillIndex < index ? 1 : fillIndex === index ? time / durations[fillIndex] : 0})`;
        });
        cursor.style.opacity = 0;
        tap.style.opacity = 0;

        if (index === 0) {
            setText(packageValue, time < 1550 ? '' : '500 Follower');
            packageValue.classList.toggle('placeholder', time < 1550);
            dropdown.classList.toggle('open', time >= 750 && time < 1550);
            type(fields[0], 'max.mustermann', time, 2350, 1350);
            summary.style.opacity = clamp((time - 3750) / 250);
            if (time < 950) {
                move('package-control', 'package-control', time, 0, 1, 700);
            } else if (time < 1650) {
                move('package-control', 'package-option', time, 950, 420, 1450);
            } else {
                move('package-option', 'username-control', time, 1650, 570, 2250);
            }
        } else if (index === 1) {
            type(fields[1], 'Max Mustermann', time, 600, 1200);
            type(fields[2], 'max@beispiel.de', time, 2500, 1350);
            if (time < 1950) {
                move('name-control', 'name-control', time, 0, 1, 550);
            } else if (time < 4100) {
                move('name-control', 'email-control', time, 1950, 450, 2450);
            } else {
                move('email-control', 'apple-pay', time, 4100, 650, 4850);
            }
            const pulse = clamp((time - 4850) / 600);
            applePay.style.transform = `scale(${1 + Math.sin(pulse * Math.PI) * 0.045})`;
        } else {
            const targetGain = time < 2500
                ? followerArrivals.findLast(([at]) => time >= at)[1]
                : Math.min(500, 39 + Math.floor((time - 2500) * 60 / 1000));
            followerGain = animateFollowers && time >= 2500 && !reducedMotion.matches
                ? Math.min(targetGain, followerGain + 1)
                : targetGain;
            setText(followerCount, formatNumber.format(1240 + followerGain));
        }
    };
    const syncPlayback = () => {
        cancelAnimationFrame(frame);
        frame = 0;
        previousTime = undefined;
        if (!paused && visible && !document.hidden) {
            frame = requestAnimationFrame(tick);
        }
    };
    const setPaused = (value) => {
        paused = value;
        if (!paused && previewOnly) {
            previewOnly = false;
            render();
        }
        playback.setAttribute('aria-pressed', String(paused));
        playback.setAttribute('aria-label', paused ? 'Animation abspielen' : 'Animation pausieren');
        syncPlayback();
    };
    const observeVisibility = () => {
        if (visibilityObserver) {
            visibilityObserver.disconnect();
        }
        // Wait for most of the stage, or most of a short viewport, to be visible.
        const threshold = Math.min(0.85, innerHeight * 0.85 / stage.getBoundingClientRect().height);
        visibilityObserver = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting && entry.intersectionRatio >= threshold;
            syncPlayback();
        }, {threshold});
        visibilityObserver.observe(stage);
    };
    function tick(now) {
        if (previousTime !== undefined) {
            let nextElapsed = elapsed + now - previousTime;
            // Preserve single steps and the final hold even when frames are delayed.
            if (currentScene === 2 && followerGain < 500 && !reducedMotion.matches) {
                nextElapsed = Math.min(nextElapsed, starts[2] + followerFinish);
            }
            elapsed = nextElapsed % total;
            render(true);
        }
        previousTime = now;
        frame = requestAnimationFrame(tick);
    }

    navigation.forEach((button) => button.addEventListener('click', () => {
        const index = Number(button.dataset.step);
        elapsed = starts[index];
        previewOnly = paused;
        previousTime = undefined;
        render();
        root.querySelector('#order-process-step-announcement').textContent = button.getAttribute('aria-label');
    }));
    playback.addEventListener('click', () => setPaused(!paused));
    // Keyboard users keep the current scene until they explicitly restart playback.
    root.addEventListener('focusin', (event) => {
        if (event.target !== playback && event.target.matches(':focus-visible')) {
            setPaused(true);
        }
    });
    const onMotionChange = (event) => {
        if (event.matches === reduceMotionPreferred) {
            return;
        }
        reduceMotionPreferred = event.matches;
        setPaused(event.matches);
        render();
    };
    if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', onMotionChange);
    } else {
        reducedMotion.addListener(onMotionChange);
    }
    // The initialization-time query can miss later change events; retain a fresh query after load.
    const subscribeLiveMotionChanges = () => {
        liveReducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        if (typeof liveReducedMotion.addEventListener === 'function') {
            liveReducedMotion.addEventListener('change', onMotionChange);
        } else {
            liveReducedMotion.addListener(onMotionChange);
        }
        onMotionChange(liveReducedMotion);
    };
    const scheduleLiveMotionSubscription = () => window.setTimeout(subscribeLiveMotionChanges, 0);
    if (document.readyState === 'complete') {
        scheduleLiveMotionSubscription();
    } else {
        window.addEventListener('load', scheduleLiveMotionSubscription, {once: true});
    }
    new ResizeObserver(() => {
        measure();
        observeVisibility();
    }).observe(stage);
    window.addEventListener('resize', observeVisibility);
    document.addEventListener('visibilitychange', syncPlayback);
    setPaused(paused);
    render();
    document.fonts.ready.then(measure);
};

const initHomepage = () => {
    homepageHero.init();
    window.postponedExecution(initHomepageReviews);
    window.postponedExecution(initProductComparison);
    window.postponedExecution(initOrderProcess);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHomepage);
} else {
    initHomepage();
}
